import React, { useState } from "react";
import { motion } from "framer-motion";
import { ImageViewer } from "@/components/ui/image-viewer";
import { ScatteredImageCarousel } from "./ScatteredImageCarousel";
import { Pencil, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ImageMessageProps {
  images: string[];
  messageId: string;
  onEdit?: (messageId: string) => void;
  role?: "user" | "assistant";
}

interface SelectedImage {
  url: string;
  messageId: string;
  index: number;
}

export const ImageMessage: React.FC<ImageMessageProps> = ({
  images,
  messageId,
  onEdit,
  role = "assistant",
}) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(
    null
  );

  // Use ScatteredImageCarousel for multiple images
  if (images.length > 1) {
    return (
      <ScatteredImageCarousel
        images={images}
        messageId={messageId}
        onEdit={onEdit}
        role={role}
      />
    );
  }

  // Single image: Use simple viewer
  // User mesajlarında görsel text'ten önce geldiği için mb-2 (alt boşluk)
  // Assistant mesajlarında görsel text'ten sonra geldiği için mt-2 (üst boşluk)
  const marginClass = role === "user" ? "mb-2" : "mt-2";

  return (
    <>
      <div
        className={cn(
          "w-full max-w-[220px] @min-[560px]/chat:max-w-[300px]",
          marginClass,
        )}
      >
        <div
          className="relative cursor-pointer transition-all duration-300 hover:scale-[0.98] aspect-square rounded-lg overflow-hidden border border-border hover:border-foreground"
          onClick={() =>
            setSelectedImage({
              url: images[0],
              messageId,
              index: 0,
            })
          }
        >
          <motion.img
            layoutId={`${messageId}-image-0`}
            src={images[0]}
            alt="Generated image"
            className="w-full h-full object-cover"
          />
        </div>

        {/* 🎨 Edit button below image */}
        {onEdit && (
          <Button
            variant="outline"
            className="mt-2  bg-transparent border border-border font-medium shadow-sm rounded-xl"
            onClick={() => onEdit(messageId)}
          >
            <div className="flex items-center justify-between w-full px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-foreground" />
                <span className="text-sm text-foreground font-medium">
                  {t("common.edit")}
                </span>
              </div>
              <ChevronRight className="h-4 w-4 text-foreground" />
            </div>
          </Button>
        )}
      </div>

      {selectedImage && (
        <ImageViewer
          image={selectedImage.url}
          messageId={selectedImage.messageId}
          imageIndex={selectedImage.index}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};
